import React, { useState, useEffect } from "react";
import {
  MemoryRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Sidebar from "./components/common/Sidebar";
import Header from "./pages/Header/Header.js";
import Home from "./pages/Home/Home.js";
import ViewAndUpload from "./pages/ViewAndUpload/ViewAndUpload.js";
import TransactionDetails from "./pages/TransactionDetails/TransactionDetails.js";
import TransactionChainPage from "./pages/TransactionChain/TransactionChain.js";
import RuntimeValidation from "./pages/RuntimeValidation/RuntimeValidation";
import Monitoring from "./pages/Monitoring/Monitoring.js";
import Reporting from "./pages/Reporting/Reporting.js";
import ConfigureBot from "./pages/ConfigureBot/ConfigureBot.js";
import IntegratedTools from "./pages/IntegratedTools/IntegratedTools.js";
import AdminPanel from "./pages/AdminPanel/AdminPanel.js";
import Login from "./pages/Login/Login.js";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword.js";
import CustomerProfiling from "./pages/CustomerProfiling/CustomerProfiling.js";
import DocumentManager from "./pages/DocumentManager/DocumentManager.js";
import Signup from "./pages/SignUp/Signup.js";
import ExistingCustomerDocs from "./pages/ExistingCustomerDocs/ExistingCustomerDocs.js";
import PaymentMethodSetup from "./pages/PaymentMethodSetup/PaymentMethodSetup.js";
import "./styles/App.css";

// Use environment variable for API URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!API_BASE_URL) {
  throw new Error("API_BASE_URL is not defined in .env");
}

// Scroll to top component for route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Protected route
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem("authToken");
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Admin restricted route
const RestrictedRoute = ({ children, requiredRole }) => {
  const userRole = localStorage.getItem("userRole") || "user";
  return userRole === requiredRole ? (
    children
  ) : (
    <div style={{ padding: "24px", textAlign: "center" }}>
      <h2>Access Denied</h2>
      <p>Only admins can access this page.</p>
    </div>
  );
};

const Layout = ({ children, collapsed, setCollapsed, alerts, onAcknowledgeAlert, theme, onToggleTheme }) => (
  <div className={`app-container ${collapsed ? "sidebar-collapsed" : ""} ${theme}-theme`}>
    <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} theme={theme} onToggleTheme={onToggleTheme} />
    <div className={`main-content ${collapsed ? "collapsed" : ""}`} style={{ position: 'relative' }}>
      <Header theme={theme} />
      <div>
        {React.cloneElement(children, { alerts, onAcknowledgeAlert, theme })}
      </div>
      {/* Toast Container positioned within main content */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ 
          fontSize: "14px",
          position: "absolute",
          bottom: "20px",
          right: "20px",
          zIndex: 9999
        }}
      />
    </div>
  </div>
);

function App() {
  const [collapsed, setCollapsed] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);
  const [theme, setTheme] = useState(() => {
    // Initialize theme from localStorage or default to 'dark'
    return localStorage.getItem('theme') || 'dark';
  });

  // Apply theme to document root
  useEffect(() => {
    document.documentElement.className = theme === 'light' ? 'light-theme' : 'dark-theme';
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Toggle theme function
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  // Fetch alerts on mount and when user logs in
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      fetchAlerts();
      // Set up periodic refresh every 5 minutes
      const intervalId = setInterval(fetchAlerts, 5 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, []);

  // Centralized alert fetching
  const fetchAlerts = async () => {
    setIsLoadingAlerts(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.warn("No authentication token found. Skipping alert fetch.");
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/alerts/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const mappedAlerts = response.data.map((alert) => ({
        id: alert.id.toString(),
        transaction_id: alert.transaction_id?.toString() || null,
        type: alert.alert_type || "Unknown",
        message: alert.message || "No details available",
        date: new Date(alert.created_at).toISOString().slice(0, 10),
        status: alert.acknowledged === null ? "Open" : alert.acknowledged ? "Acknowledged" : "Open",
        raw: alert, // Keep raw data for reference
      }));

      console.log("Fetched alerts:", mappedAlerts);
      setAlerts(mappedAlerts);
    } catch (err) {
      console.error("Error fetching alerts:", err);
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        toast.error(
          "Failed to load alerts: " + (err.response?.data?.message || err.message),
          {
            position: "bottom-right",
            autoClose: 3000,
            style: {
              background: "#f8d7da",
              color: "#721c24",
              borderRadius: "8px",
              fontSize: "14px",
            },
          }
        );
      }
    } finally {
      setIsLoadingAlerts(false);
    }
  };

  // Centralized alert acknowledgment
  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No authentication token found.");
      }

      await axios.post(
        `${API_BASE_URL}/alerts/${alertId}/acknowledge`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setAlerts((prevAlerts) =>
        prevAlerts.map((alert) =>
          alert.id === alertId ? { ...alert, status: "Acknowledged" } : alert
        )
      );

      toast.success("Alert acknowledged successfully.", {
        position: "bottom-right",
        autoClose: 1500,
        style: {
          background: "#d4edda",
          color: "#155724",
          borderRadius: "8px",
          fontSize: "14px",
        },
      });
    } catch (err) {
      console.error("Error acknowledging alert:", err);
      toast.error(
        err.message === "No authentication token found."
          ? "Please log in to acknowledge alerts."
          : "Failed to acknowledge alert: " + (err.response?.data?.message || err.message),
        {
          position: "bottom-right",
          autoClose: 3000,
          style: {
            background: "#f8d7da",
            color: "#721c24",
            borderRadius: "8px",
            fontSize: "14px",
          },
        }
      );
    }
  };

  // Function to add new alerts
  const addAlerts = (newAlerts) => {
    setAlerts((prevAlerts) => [...prevAlerts, ...newAlerts]);
  };

  // Expose global function for validation completion callbacks
  useEffect(() => {
    window.onValidationsComplete = (transactionId, validations) => {
      const newAlerts = validations
        .filter((v) => v.status === "Fail" || v.status === "FAILED")
        .map((v, index) => ({
          id: `temp-${Date.now()}-${index}`,
          transaction_id: transactionId.toString(),
          type: "High Risk",
          message: `${v.checkName || v.validation_check}: ${v.details || v.reason_evidence}`,
          date: new Date().toISOString().slice(0, 10),
          status: "Open",
        }));

      if (newAlerts.length > 0) {
        addAlerts(newAlerts);
        toast.info(`${newAlerts.length} new alert(s) created from validation results.`, {
          position: "bottom-right",
          autoClose: 2000,
          style: {
            background: "#d1ecf1",
            color: "#0c5460",
            borderRadius: "8px",
            fontSize: "14px",
          },
        });
      }
    };

    return () => {
      window.onValidationsComplete = null;
    };
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<Login onLoginSuccess={fetchAlerts} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <Home />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/customer-profiling"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <CustomerProfiling />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-method-setup"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <PaymentMethodSetup />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-and-upload"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <ViewAndUpload />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/transaction-details/:transactionId"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <TransactionDetails />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/transaction-chain"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <TransactionChainPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/runtime-validation"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <RuntimeValidation />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/monitoring"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <Monitoring />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <Reporting />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/configure-bot"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <ConfigureBot />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/document-edit"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <div>Document Edit (Under Construction)</div>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrated-tools"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <IntegratedTools />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/document-manager"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <DocumentManager collapsed={collapsed} />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/customer-documents/:customerId"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <ExistingCustomerDocs />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-panel"
          element={
            <ProtectedRoute>
              <RestrictedRoute requiredRole="admin">
                <Layout
                  collapsed={collapsed}
                  setCollapsed={setCollapsed}
                  alerts={alerts}
                  onAcknowledgeAlert={handleAcknowledgeAlert}
                  theme={theme}
                  onToggleTheme={toggleTheme}
                >
                  <AdminPanel />
                </Layout>
              </RestrictedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <Layout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                alerts={alerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                theme={theme}
                onToggleTheme={toggleTheme}
              >
                <div>404 - Page Not Found</div>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;